import React from "react";
import { useLazyLoadQuery } from "react-relay";
import TextArea from "react-textarea-autosize";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faSlidersH, faSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { startCase } from "lodash";
import { graphql } from "relay-runtime";

import { DismissibleElementsContext } from "~/common/DismissibleElements";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { SideBarItem } from "~/stream/SideBar/SideBarItem";
import { Button, Tooltip } from "~/ui";
import { IconButton, ToggleGroup } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";
import DismissibleTooltip from "~/ui/Tooltip/DismissibleTooltip";

import type { agendaBuilderSettingsQuery } from "~/__generated__/agendaBuilderSettingsQuery.graphql";
import type {
	AgendaBuilderTone,
	agendaBuilderSetToneAndContextMutation as ToneAndContextMutationType,
} from "~/__generated__/agendaBuilderSetToneAndContextMutation.graphql";
import type { NoteCardGenerateAgendaHeadersMutation$data } from "~/__generated__/NoteCardGenerateAgendaHeadersMutation.graphql";

import stylesDetails from "~/stream/StreamSettings/Details.module.css";

import styles from "./AgendaBuilder.module.css";

const AGENDA_BUILDER_CONTEXT_MAX_LENGTH = 300;
export const AGENDA_BUILDER_FEATURE_FLAG = "ai-agenda-builder";

const setToneAndContextMutation = graphql`
	mutation agendaBuilderSetToneAndContextMutation($streamId: ID!, $tone: AgendaBuilderTone!, $context: String!) {
		noteStreamSetAgendaBuilderToneAndContext(streamId: $streamId, tone: $tone, context: $context) {
			stream {
				id
				agendaBuilderTone
				agendaBuilderContext
			}
		}
	}
`;

export type HeaderContentType = NonNullable<
	NoteCardGenerateAgendaHeadersMutation$data["generateAgendaHeaders"]
>["headers"][0];

type HeaderProps = Omit<HeaderContentType, "talkingPoint"> & {
	onClick: () => void;
	showTooltip?: boolean;
};

const AgendaBuilderHeaderKey = "AgendaBuilder__Header";

export function Header({ content, subheader, onClick, showTooltip }: HeaderProps) {
	const { dismiss, hasDismissed } = React.useContext(DismissibleElementsContext);
	const dismissHeaderTooltip = React.useCallback(() => {
		dismiss(AgendaBuilderHeaderKey);
	}, [dismiss]);

	const onClickHeader = React.useCallback(() => {
		dismissHeaderTooltip();
		onClick();
	}, [dismissHeaderTooltip, onClick]);

	const showHeaderTooltip = React.useMemo(
		() => showTooltip && !hasDismissed(AgendaBuilderHeaderKey),
		[hasDismissed, showTooltip],
	);

	return (
		<Tooltip
			visible={showHeaderTooltip}
			interactive
			content={
				<DismissibleTooltip
					dismiss={dismissHeaderTooltip}
					icon={faSparkles}
					text="Click to add the AI-generated headings to your agenda."
				/>
			}
		>
			<button className={styles.button} onClick={onClickHeader} data-test-id={`${AGENDA_BUILDER_FEATURE_FLAG}-header`}>
				<FontAwesomeIcon icon={faPlus} className={cx(styles.icon, styles.plusIcon)} />
				<FontAwesomeIcon icon={faSparkles} className={cx(styles.icon, styles.sparklesIcon)} />
				<h1 className={styles.header}>{content}</h1>
				{subheader && <p className={styles.subheader}>{subheader}</p>}
			</button>
		</Tooltip>
	);
}

const TONE_OPTIONS: AgendaBuilderTone[] = ["CASUAL", "NEUTRAL", "FORMAL"];

const settingsQuery = graphql`
	query agendaBuilderSettingsQuery($streamId: ID!) {
		stream: node(id: $streamId) {
			... on NoteStream {
				agendaBuilderTone
				agendaBuilderContext
			}
		}
	}
`;

export function Settings({
	streamId,
	onRegenerateClick: _onRegenerateClick,
	disabled,
}: {
	streamId: string;
	disabled: boolean;
	onRegenerateClick: () => void;
}) {
	const data = useLazyLoadQuery<agendaBuilderSettingsQuery>(settingsQuery, { streamId });

	const [loading, setLoading] = React.useState(false);
	const [tone, setTone] = React.useState<AgendaBuilderTone>(data.stream?.agendaBuilderTone ?? "NEUTRAL");
	const [context, setContext] = React.useState<string>(data.stream?.agendaBuilderContext ?? "");
	const onContextChange = React.useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => setContext(event.target.value),
		[],
	);

	const onRegenerateClick = React.useCallback(async () => {
		if (loading) {
			return;
		}
		setLoading(true);

		if (context.length > AGENDA_BUILDER_CONTEXT_MAX_LENGTH) {
			toast.error(`Maximum context length is ${AGENDA_BUILDER_CONTEXT_MAX_LENGTH} characteres`, { title: "Error" });
			return;
		}

		try {
			const { noteStreamSetAgendaBuilderToneAndContext } = await commitMutationPromise<ToneAndContextMutationType>(
				environment,
				{
					mutation: setToneAndContextMutation,
					variables: { streamId, tone, context: context.trim() },
				},
			);

			if (noteStreamSetAgendaBuilderToneAndContext?.stream.id) {
				toast.success("Settings updated.", { title: "Success" });

				_onRegenerateClick();
			} else {
				toast.error("Something went wrong, please try again.", { title: "Failed regenerating content" });
			}
		} catch (errors) {
			toast.error("Something went wrong, please try again.", { title: "Failed regenerating content" });
		} finally {
			setLoading(false);
		}
	}, [loading, streamId, context, tone, _onRegenerateClick]);

	return (
		<SideBarItem className={styles.settings}>
			<SideBarItem.Header>
				<SideBarItem.Title>Settings</SideBarItem.Title>
			</SideBarItem.Header>
			<SideBarItem.Body className={styles.body}>
				<label className={styles.label}>Tone</label>
				<ToggleGroup
					value={tone}
					options={TONE_OPTIONS.map(option => ({ value: option, label: startCase(option.toLowerCase()) }))}
					onValueChange={setTone}
					className={styles.toggles}
				/>

				<label className={styles.label}>
					Tell us more about your meeting. <em>{`${context.length}/300`}</em>
				</label>
				<TextArea
					className={cx(stylesDetails.textArea, styles.textField)}
					value={context}
					onChange={onContextChange}
					placeholder="Eg. Discuss project updates, review OKRs, resolve blockers"
					maxLength={AGENDA_BUILDER_CONTEXT_MAX_LENGTH}
					minRows={6}
					maxRows={6}
				/>
				<p className={styles.tipText}>Add context to your meeting to get better suggestions.</p>

				<Button
					onClick={
						tone === data.stream?.agendaBuilderContext && context === data.stream.agendaBuilderContext
							? _onRegenerateClick
							: onRegenerateClick
					}
					variant="primary"
					size="slim"
					disabled={loading || disabled}
					data-test-id={`${AGENDA_BUILDER_FEATURE_FLAG}-settings-regenerate`}
				>
					Regenerate
				</Button>
			</SideBarItem.Body>
		</SideBarItem>
	);
}

export function AgendaBuilderBanner({
	onSettingsClick,
	onClose,
	className,
}: {
	onSettingsClick: () => void;
	onClose: () => void;
	className?: string;
}) {
	return (
		<article className={cx(styles.banner, className)}>
			<FontAwesomeIcon icon={faSparkles} className={styles.icon} />
			<div className={styles.bannerText}>
				<p>Click on the suggested headings to add them to your agenda</p>
			</div>
			<IconButton title="Back" icon={faSlidersH} onClick={onSettingsClick} className={styles.settingsBtn} />
			<Button onClick={onClose} variant="primary" size="slim">
				Done
			</Button>
		</article>
	);
}

export function ErrorBanner({
	onClose,
	onRetry,
	className,
}: {
	onClose: () => void;
	onRetry: () => void;
	className?: string;
}) {
	return (
		<article className={cx(styles.banner, styles.errorBanner, className)}>
			<div className={styles.bannerText}>AI-generated agenda could not be generated. Please try again.</div>
			<Button onClick={onRetry} variant="primary" size="slim">
				Retry
			</Button>
			<IconButton title="Close" icon={faTimes} onClick={onClose} className={styles.closeBtn} />
		</article>
	);
}
